/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react'

import { useCallbackRef } from '../../hooks'
import useTimeout from '../../hooks/use-timeout'
import type { ColorStateKey } from '../../theme-provider/types'
import { EMPTY_OBJECT, get, omit } from '../../utils/object'
import { getGrid, omitGrid } from '../../utils/props-utils'
import type { FlexProps } from '../_primitives/flex/types'
import type { InputProps } from '../_primitives/input/types'
import Text from '../_primitives/text/text'
import FormField from '../form-field'

const DEBUG = process.env.NODE_ENV !== 'production' && false

const bindFns =
  (...fns: Array<Function>) =>
  (...args: any) => {
    fns.forEach(fn => fn && fn(...args))
  }

const Indicator = <Text color="danger">*</Text>

type MapProps = {
  a11yId?: string
  a11yLabel?: string
  state?: ColorStateKey
  isRequire?: boolean
  checked?: boolean
  isInvalid?: boolean
  value?: any
  onBlur?: () => void
  onChange?: () => void
  onFocus?: () => void
  componentsProps?: {
    input?: InputProps
    rootInput?: FlexProps
  }
}

export function formikField(
  Component: any,
  {
    disableBlurEvent = false,
    disableFocusEvent = false,
    hasFieldWrapper = false,
    isCheckbox = false,
    isCheckboxGroup = false,
    isAutoComplete = false,
    isSelectMenu = false,
    useValue = false
  } = {}
) {
  return function FormikField({
    field = {},
    form = {},
    label,
    labelProps,
    required,
    helperText,
    formatValue,
    options,
    onBlur: onBlurProp,
    onChange: onChangeProp,
    onFocus: onFocusProp,
    ...props
  }: any) {
    const {
      name,
      onBlur: fieldOnBlur,
      onChange: fieldOnChange,
      onFocus: fieldOnFocus
    } = field

    const { setFieldTouched: _setFieldTouched, setFieldValue: _setFieldValue } =
      form
    // use ref to decouple formik non-memorable props
    const setFieldValue = useCallbackRef(_setFieldValue)
    const setFieldTouched = useCallbackRef(_setFieldTouched)

    const isTouched = get(form, `touched.${name}`)
    const error = get(form, `errors.${name}`)
    const value = get(form, `values.${name}`)
    let validationText = isTouched && error ? error : undefined
    if (props.validationText) {
      validationText = props.validationText
    }

    let state: ColorStateKey | undefined =
      isTouched && error ? 'danger' : undefined
    if (props.state) {
      state = props.state
    }

    const { componentsProps = EMPTY_OBJECT } = props
    let overrideProps: MapProps = isAutoComplete
      ? {
          componentsProps: {
            input: {
              state
            },
            ...componentsProps
          }
        }
      : { state }

    if (hasFieldWrapper) {
      overrideProps = {
        ...overrideProps,
        a11yId: name,
        a11yLabel: label,
        isRequire: required
      }
    }

    if (isCheckbox) {
      let checked = false
      if (value) {
        checked = Boolean(value)
      }
      if (props.checked) {
        checked = props.checked
      }
      overrideProps = { ...overrideProps, checked, isInvalid: !!validationText }
    }
    const delayExecute = useTimeout(0)
    const { onBlur, onChange, onFocus } = useMemo(() => {
      // eslint-disable-next-line no-console
      // console.log(` ---- formik useMemo [${name}] ----\r `)
      let onBlur = fieldOnBlur
      let onChange = fieldOnChange
      const onFocus = fieldOnFocus
      if (isSelectMenu || isAutoComplete) {
        if (isAutoComplete) {
          onBlur = () => {
            // eslint-disable-next-line no-console
            // console.log(`--- formikField onBlur ---`)
            delayExecute(() => setFieldTouched(name))
          }
          onChange = (_e: any, newValues: any) => {
            setFieldValue(name, newValues)
          }
        } else {
          onBlur = () => setFieldTouched(name)
          onChange = (_value: any, _option: any, newValues: any) =>
            setFieldValue(name, newValues)
        }
      }
      if (isCheckboxGroup) {
        onChange = (value: any) => setFieldValue(name, value)
        onBlur = () => setFieldTouched(name)
      }
      if (useValue) {
        onChange = (value: any) => setFieldValue(name, value)
        if (isAutoComplete) {
          delayExecute(() => setFieldTouched(name))
        }
      }
      return {
        onBlur: bindFns(disableBlurEvent ? () => {} : onBlur, onBlurProp),
        onChange: bindFns(onChange, onChangeProp),
        onFocus: bindFns(disableFocusEvent ? () => {} : onFocus, onFocusProp)
      }
    }, [
      fieldOnBlur,
      fieldOnChange,
      fieldOnFocus,
      onBlurProp,
      onChangeProp,
      onFocusProp,
      delayExecute,
      setFieldTouched,
      name,
      setFieldValue
    ])

    overrideProps = {
      ...overrideProps,
      ...(formatValue
        ? {
            value: formatValue(value)
          }
        : useValue
          ? { value }
          : {}),
      onBlur,
      onChange,
      onFocus
    }

    if (DEBUG && name === 'compSetPermissions') {
      // eslint-disable-next-line no-console
      console.log(
        ` ---- formik adapter [${name}] ----\r\n   form: ${JSON.stringify(
          form,
          undefined,
          2
        )}\r\n  -------- \r\n   field: ${JSON.stringify(
          omit(field, ['name']),
          undefined,
          2
        )}\r\n  -------- \r\n   overrideProps: ${JSON.stringify(
          omit(overrideProps, ['onBlur', 'onChange', 'onFocus']),
          undefined,
          2
        )} \r\n -------- `
      )
    }

    return hasFieldWrapper ? (
      <FormField
        label={label}
        labelProps={labelProps}
        labelIndicator={required ? Indicator : null}
        validationText={validationText}
        helperText={helperText}
        {...getGrid(props)}
      >
        <Component
          {...(isAutoComplete ? { options, isInvalid: !!validationText } : {})}
          {...omitGrid(props)}
          {...field}
          {...overrideProps}
        />
      </FormField>
    ) : (
      <Component
        {...(isAutoComplete ? { options } : {})}
        {...props}
        {...field}
        {...{
          label,
          labelProps,
          isRequired: required,
          validationText,
          state: validationText ? 'danger' : undefined,
          ...overrideProps
        }}
      />
    )
  }
}
